import {gql} from '@apollo/client';
import {objectFromEntries} from '@benchling/js-utils';
import {DocumentNode} from 'graphql';
import React from 'react';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import * as benchlingUtil from 'BenchlingUtil';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST, graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  ExecuteRequestsQuery,
  ExecuteRequestsQueryVariables,
} from 'insights/analytics-queries/overflow-menu/__generated/ExecuteRequestsQuery';
import {LegacyRunQueryState, RunQueryOutputLegacyData} from 'insights/warehouse/AnalyticsWarehouseTypes';
import cellToItemIds from 'insights/warehouse/cellToItemIds';
import notifications from 'notifications';
import canRequestsBeExecuted from 'requests/canRequestsBeExecuted';
import ExecuteRequestFlow from 'requests/execution/ExecuteRequestFlow';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import Menu from 'ui-library/menus/Menu';
import {OptionContentType} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import SectionHeader from 'ui-library/menus/SectionHeader';
import * as ModalContainer from 'ui-library/ModalContainer';
import APIUtil from 'util/APIUtil';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {RequestGraphQL} from '../../../../graphql-fragments/requests/__generated/RequestGraphQL';

type Request = ExtractByTypename<ExecuteRequestsQuery['requests'][number], 'RequestNode'>;

export const EXECUTE_REQUESTS_QUERY: DocumentNode = gql`
  query ExecuteRequestsQuery($requestIds: [StrictString!]!) @skipAudit @engComponent(name: INSIGHTS) {
    requests(ids: $requestIds) {
      ... on RequestNode {
        ...RequestGraphQL
        schema {
          ...RequestSchemaGraphQL
        }
        sampleGroups {
          samples {
            id
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('Request').fragment}
  ${getFragmentWrapper('RequestSchema').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface RequestColumn {
  name: string;
  uniqueRequestIds: Array<string>;
}

interface GetExecuteRequestOptionProps {
  runQueryState: LegacyRunQueryState;
}

export interface GetExecuteRequestOptionMenuProps {
  columns: Array<RequestColumn>;
}

async function handleSelectColumn({uniqueRequestIds}: RequestColumn): Promise<void> {
  assertAndContinue(
    uniqueRequestIds.length === new Set(uniqueRequestIds).size,
    'Column items have duplicates',
    {
      uniqueRequestIds,
    }
  );
  const INFO_TOAST_ID = 'checking-requests-executable';
  notifications.info('Checking whether requests are executable...', {id: INFO_TOAST_ID, duration: 0});

  const result = await doQuerySafe<ExecuteRequestsQuery, ExecuteRequestsQueryVariables>(
    {query: EXECUTE_REQUESTS_QUERY},
    {variables: {requestIds: uniqueRequestIds}}
  );
  if (result.requests) {
    const inaccessibleRequests = result.requests.filter((request) => isInaccessible(request));
    if (inaccessibleRequests.length > 0) {
      notifications.error(
        `You do not have permission to execute ${benchlingUtil.pluralize(
          inaccessibleRequests.length,
          'request'
        )} ${inaccessibleRequests
          .map((request) => (request as InaccessibleInfoGraphQL).invalidId)
          .join(', ')}`
      );
      return;
    }
    const requestSchemaByRequestId = objectFromEntries(
      result.requests.map((request: Request) => [request.id, graphQLToREST('RequestSchema', request.schema)])
    );
    const requestIdToRequestHasSamples = objectFromEntries(
      result.requests.map((request: Request) => [
        request.id,
        request.sampleGroups.filter((sampleGroup) => sampleGroup.samples.length !== 0).length > 0,
      ])
    );
    const requests = graphQLArrayToREST('Request', result.requests as Array<RequestGraphQL>);
    const {explanationMessage} = canRequestsBeExecuted({
      requests,
      requestSchemaByRequestId,
      requestIdToRequestHasSamples,
    });

    notifications.close(INFO_TOAST_ID);
    if (explanationMessage == null) {
      ModalContainer.open(
        ExecuteRequestFlow,
        <ExecuteRequestFlow
          requests={requests}
          initialFilters={[]}
          requestSchemasByRequestId={requestSchemaByRequestId}
          onRequestClose={() => {
            ModalContainer.close(ExecuteRequestFlow);
          }}
        />
      );
    } else {
      notifications.error(explanationMessage);
    }
  }
}

function runQueryOutputToRequestIds(
  runQueryOutput: RunQueryOutputLegacyData,
  columnIndex: number
): Array<string> {
  const allIds = runQueryOutput.rows.flatMap(
    (row) => cellToItemIds(row[columnIndex], (cellContent) => APIUtil.isRequest(cellContent)) ?? []
  );
  return _.unique(allIds);
}

/**
 * Convert columns with request IDs into menu options, skipping columns with no request IDs.
 * Exported for test only.
 */
export function runQueryOutputToRequestColumns(
  runQueryOutput: RunQueryOutputLegacyData
): Array<RequestColumn> {
  return runQueryOutput.headers
    .map((header, columnIndex) => ({
      name: header,
      uniqueRequestIds: runQueryOutputToRequestIds(runQueryOutput, columnIndex),
    }))
    .filter((column) => column.uniqueRequestIds.length > 0);
}

function columnToOption(column: RequestColumn, columnIndex: number): JSX.Element {
  assertAndContinue(column.uniqueRequestIds.length > 0, 'Column has no items', {name: column.name});
  return (
    <Option
      key={`${columnIndex}-${column.name}`}
      contentType={OptionContentType.PLAIN}
      onSelect={handleSelectColumn}
      shouldEllipsize={true}
      value={column}
    >
      {column.name}
    </Option>
  );
}

function getExecuteRequestOptionMenu(props: GetExecuteRequestOptionMenuProps): JSX.Element {
  return (
    <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM}>
      <Section>
        <SectionHeader>Column</SectionHeader>
        {props.columns.length ? (
          props.columns.map((column, columnIndex) => columnToOption(column, columnIndex))
        ) : (
          <Option contentType={OptionContentType.PLAIN} isDisabled={true}>
            No columns for requests found
          </Option>
        )}
      </Section>
    </Menu>
  );
}

export default function getExecuteRequestOption(props: GetExecuteRequestOptionProps): JSX.Element {
  return (
    <Option icon="request" isDisabled={props.runQueryState.status !== AsyncStatus.SUCCESS}>
      Execute request
      {props.runQueryState.status === AsyncStatus.SUCCESS &&
        getExecuteRequestOptionMenu({columns: runQueryOutputToRequestColumns(props.runQueryState.data)})}
    </Option>
  );
}
